/** Clients list — DataTable. */

import { useQuery } from "@tanstack/react-query";
import type { ColumnDef } from "@tanstack/react-table";
import { Search } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { DataTable } from "@/components/shared/DataTable";
import { api } from "@/lib/api";
import { formatDate, formatPrice } from "@/lib/utils";

interface ClientRow {
  id: number;
  phone: string;
  full_name: string | null;
  email: string | null;
  is_blocked: boolean;
  language: string;
  orders_count: number;
  total_spent: number;
  last_order_at: string | null;
  created_at: string;
}

export function ClientsListPage(): JSX.Element {
  const { t } = useTranslation();
  const [page, setPage] = useState(1);
  const [perPage] = useState(25);
  const [q, setQ] = useState("");

  const { data, isLoading } = useQuery({
    queryKey: ["admin-clients", page, perPage, q],
    queryFn: async () => {
      const { data } = await api.get("/clients", {
        params: { page, per_page: perPage, q: q || undefined },
      });
      return data;
    },
  });

  const columns: ColumnDef<ClientRow>[] = [
    {
      accessorKey: "phone",
      header: "Телефон",
      cell: ({ row }) => (
        <Link
          to={`/clients/${row.original.id}`}
          className="font-medium hover:underline"
        >
          {row.original.phone}
        </Link>
      ),
    },
    { accessorKey: "full_name", header: "ФИО", cell: ({ row }) => row.original.full_name ?? "—" },
    { accessorKey: "language", header: "Язык" },
    {
      accessorKey: "orders_count",
      header: "Заказов",
      cell: ({ row }) => row.original.orders_count,
    },
    {
      accessorKey: "total_spent",
      header: "Потрачено",
      cell: ({ row }) =>
        row.original.total_spent > 0 ? formatPrice(row.original.total_spent) : "—",
    },
    {
      accessorKey: "last_order_at",
      header: "Последний заказ",
      cell: ({ row }) =>
        row.original.last_order_at ? formatDate(row.original.last_order_at) : "—",
    },
    {
      accessorKey: "is_blocked",
      header: "Статус",
      cell: ({ row }) =>
        row.original.is_blocked ? (
          <Badge variant="destructive">Blocked</Badge>
        ) : (
          <Badge variant="success">Active</Badge>
        ),
    },
  ];

  return (
    <div className="space-y-4">
      <h1 className="text-2xl font-heading">{t("nav.clients")}</h1>

      <div className="relative w-72">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <Input
          placeholder="Phone / ФИО / email"
          value={q}
          onChange={(e) => {
            setQ(e.target.value);
            setPage(1);
          }}
          className="pl-9"
        />
      </div>

      <DataTable
        columns={columns}
        data={data?.data ?? []}
        total={data?.pagination?.total ?? 0}
        page={page}
        pageSize={perPage}
        isLoading={isLoading}
        onPageChange={setPage}
      />
    </div>
  );
}
